import React from 'react';
import { ShieldAlert, Coins, CheckCircle2, Loader2 } from 'lucide-react';
import { at } from '../../../i18nAdmin';
import { Language } from '../../../types';

interface ActionModalProps {
    isOpen: boolean;
    isAnimating: boolean;
    isPending: boolean;
    type: string;
    title: string;
    description: string;
    confirmLabel: string;
    isDanger: boolean;
    awardAmount: number;
    setAwardAmount: (amount: number) => void;
    awardReason: string;
    setAwardReason: (reason: string) => void;
    onClose: () => void;
    onConfirm: () => void;
    lang: Language;
}

export const AdminActionModal: React.FC<ActionModalProps> = ({
    isOpen, isAnimating, isPending, type, title, description, confirmLabel, isDanger,
    awardAmount, setAwardAmount, awardReason, setAwardReason, onClose, onConfirm, lang
}) => {
    if (!isOpen) return null;

    return (
        <div className={`fixed inset-0 z-[450] flex items-center justify-center p-6 transition-opacity duration-500 ${isAnimating ? 'opacity-100' : 'opacity-0'}`}>
            <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose}></div>
            <div className={`relative bg-white w-full max-w-sm rounded-[32px] shadow-2xl p-8 transition-transform duration-500 ${isAnimating ? 'scale-100' : 'scale-90'}`}>
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 ${isDanger ? 'bg-rose-50 text-rose-500' : 'bg-emerald-50 text-emerald-500'}`}>
                    {isDanger ? <ShieldAlert size={28} /> : type === 'AWARD_POINTS' ? <Coins size={28} className="text-amber-500" /> : <CheckCircle2 size={28} />}
                </div>
                <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight mb-2">{title}</h3>
                <p className="text-sm text-slate-500 font-medium leading-relaxed mb-8">{description}</p>
                
                {type === 'AWARD_POINTS' && (
                    <div className="space-y-4 mb-8 text-left">
                        <div>
                            <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-1.5 block">{at(lang, 'awardAmount')}</label>
                            <input 
                                type="number" 
                                className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 font-bold text-slate-900 focus:bg-white transition-all outline-none"
                                value={awardAmount}
                                onChange={e => setAwardAmount(Number(e.target.value))}
                            />
                        </div>
                        <div>
                            <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-1.5 block">{at(lang, 'awardReason')}</label>
                            <input 
                                type="text" 
                                className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 font-bold text-slate-900 focus:bg-white transition-all outline-none"
                                value={awardReason}
                                onChange={e => setAwardReason(e.target.value)}
                            />
                        </div>
                    </div>
                )}

                <div className="flex gap-3">
                    <button onClick={onClose} className="flex-1 py-3 text-xs font-black text-slate-400 uppercase tracking-widest">{at(lang, 'cancel')}</button>
                    <button 
                        onClick={onConfirm} 
                        disabled={isPending}
                        className={`flex-[2] py-3 rounded-xl text-xs font-black uppercase tracking-widest text-white shadow-lg active:scale-95 transition-all ${isDanger ? 'bg-rose-500 shadow-rose-100 md:hover:bg-rose-600' : 'bg-slate-900 shadow-slate-100 md:hover:bg-black'}`}
                    >
                        {isPending ? <Loader2 className="animate-spin mx-auto" size={18} /> : confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};
